/**
 * Maps domain diff events from roundDiff into discrete activity feed items (server snapshots only).
 */

import type {
  BetUserData,
  RoundSnapshot,
  SnapshotBet,
} from "@/shared/api/types/pvpRoulette"
import { getActiveBets } from "@/shared/api/types/pvpRoulette"
import { DEFAULT_GROUP_ID } from "@/shared/constants/pvpRoulette"
import { toSafeNumber } from "@/shared/lib/pvpRouletteMath"
import type { DomainDiffEvent } from "./roundDiff"

export type BubbleTrickVariant = "push" | "freeze" | "smash" | "generic"

export interface BubbleGiftEntry {
  /** Underlying betId. */
  id: string
  smallImageUrl: string | null
}

export type DerivedBubbleEvent =
  | {
      kind: "opens"
      squad: boolean
      user: BetUserData
      amount: number
      /** Present when the underlying bet(s) are TELEGRAM_GIFT. */
      gifts?: BubbleGiftEntry[]
    }
  | {
      kind: "joins"
      squad: boolean
      user: BetUserData
      amount: number
      gifts?: BubbleGiftEntry[]
    }
  | { kind: "adds"; user: BetUserData; delta: number; gifts?: BubbleGiftEntry[] }
  | { kind: "game_on" }
  | { kind: "tricks_on" }
  | { kind: "dont_blink" }
  | { kind: "trick"; user: BetUserData; trickVariant: BubbleTrickVariant }
  /** Synthetic row when a round starts in WAITING with no bets — same stack rules as other feed items. */
  | { kind: "waiting_intro"; hasSquadOption: boolean; hasTricks: boolean }

export interface ActivityFeedItem {
  id: string
  payload: DerivedBubbleEvent
}

function isSquadBet(bet: SnapshotBet): boolean {
  return bet.partyType === "TEAM" || bet.partyId === DEFAULT_GROUP_ID
}

function isActiveOrPending(bet: SnapshotBet): boolean {
  return bet.status === "ACTIVE" || bet.status === "PENDING"
}

export function findUserDataForBubble(
  snapshot: RoundSnapshot,
  userId: string
): BetUserData {
  const fromBet = getActiveBets(snapshot.bets).find(
    (b) => b.userData.userId === userId
  )
  if (fromBet) return fromBet.userData
  const party = snapshot.parties.find((p) => p.userId === userId)
  return {
    userId,
    username: party?.username ?? null,
    photoUrl: null,
  }
}

function trickTypeToVariant(type: string): BubbleTrickVariant {
  const u = type.toUpperCase()
  if (u.includes("PUSH") || u === "PUSH_WHEEL") return "push"
  if (u.includes("FREEZE")) return "freeze"
  if (u.includes("SMASH") || u.includes("CHAOS")) return "smash"
  return "generic"
}

function isGiftBet(bet: SnapshotBet): boolean {
  return bet.assetClass === "TELEGRAM_GIFT"
}

function giftEntryFromBet(bet: SnapshotBet): BubbleGiftEntry {
  return {
    id: bet.betId,
    smallImageUrl: bet.smallImageUrl ?? null,
  }
}

function deriveFromBetsAdded(
  prev: RoundSnapshot,
  added: SnapshotBet[]
): DerivedBubbleEvent[] {
  const out: DerivedBubbleEvent[] = []
  let hasAnyActive = getActiveBets(prev.bets).length > 0
  const seenUsers = new Set(
    getActiveBets(prev.bets).map((b) => b.userData.userId)
  )

  // Gift bets from the same user are coalesced into one bubble; TON bets
  // emit one bubble each.
  const giftBubbleByUser = new Map<
    string,
    Extract<DerivedBubbleEvent, { kind: "opens" | "joins" | "adds" }>
  >()

  for (const bet of added) {
    if (!isActiveOrPending(bet)) continue
    const uid = bet.userData.userId
    const squad = isSquadBet(bet)
    const amount = toSafeNumber(bet.amount, 0)

    if (isGiftBet(bet)) {
      const existing = giftBubbleByUser.get(uid)
      if (existing) {
        existing.gifts!.push(giftEntryFromBet(bet))
        if (existing.kind === "adds") existing.delta += amount
        else existing.amount += amount
        continue
      }
      const gifts = [giftEntryFromBet(bet)]
      let event: Extract<DerivedBubbleEvent, { kind: "opens" | "joins" | "adds" }>
      if (!hasAnyActive) {
        event = { kind: "opens", squad, user: bet.userData, amount, gifts }
        hasAnyActive = true
      } else if (!seenUsers.has(uid)) {
        event = { kind: "joins", squad, user: bet.userData, amount, gifts }
      } else {
        event = { kind: "adds", user: bet.userData, delta: amount, gifts }
      }
      seenUsers.add(uid)
      out.push(event)
      giftBubbleByUser.set(uid, event)
      continue
    }

    if (!hasAnyActive) {
      out.push({ kind: "opens", squad, user: bet.userData, amount })
      hasAnyActive = true
      seenUsers.add(uid)
    } else if (!seenUsers.has(uid)) {
      out.push({ kind: "joins", squad, user: bet.userData, amount })
      seenUsers.add(uid)
    } else {
      out.push({ kind: "adds", user: bet.userData, delta: amount })
    }
  }
  return out
}

function deriveFromBetsUpdated(
  prev: RoundSnapshot,
  updated: SnapshotBet[]
): DerivedBubbleEvent[] {
  const out: DerivedBubbleEvent[] = []
  for (const bet of updated) {
    if (!isActiveOrPending(bet)) continue
    const oldBet = prev.bets.find((b) => b.betId === bet.betId)
    if (!oldBet) continue
    const oldAmt = toSafeNumber(oldBet.amount, 0)
    const newAmt = toSafeNumber(bet.amount, 0)
    if (newAmt > oldAmt) {
      out.push({ kind: "adds", user: bet.userData, delta: newAmt - oldAmt })
    }
  }
  return out
}

/**
 * Produce activity items for a single snapshot transition. Caller supplies `roundDiff(prev, next)`.
 */
export function deriveBubbleEventsFromDiff(
  prev: RoundSnapshot,
  next: RoundSnapshot,
  diff: DomainDiffEvent[]
): DerivedBubbleEvent[] {
  const out: DerivedBubbleEvent[] = []

  for (const ev of diff) {
    switch (ev.type) {
      case "StatusChanged": {
        const { prev: ps, next: ns } = ev
        if (ns === "SPINNING" && ps !== "SPINNING") {
          out.push({ kind: "game_on" })
        }
        if (ns === "TRICKS" && ps !== "TRICKS") {
          out.push({ kind: "tricks_on" })
        }
        if (ns === "SLOWING" && ps !== "SLOWING") {
          out.push({ kind: "dont_blink" })
        }
        break
      }
      case "BetsAdded":
        out.push(...deriveFromBetsAdded(prev, ev.bets))
        break
      case "BetsUpdated":
        out.push(...deriveFromBetsUpdated(prev, ev.bets))
        break
      case "TrickAdded": {
        const user = findUserDataForBubble(next, ev.trick.userId)
        out.push({
          kind: "trick",
          user,
          trickVariant: trickTypeToVariant(ev.trick.type),
        })
        break
      }
      default:
        break
    }
  }

  return out
}
